import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  Grid,
  Avatar,
  Card,
  CardContent,
  useMediaQuery,
  Zoom,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Badge as RoleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { Employee, EmployeeRole, EMPLOYEE_ROLES } from '../../types';
import EmployeeFormModal from './EmployeeFormModal';
import employeeService from '../../services/employeeService';

const EmployeeList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<EmployeeRole | 'all'>('all');
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [employeeToEdit, setEmployeeToEdit] = useState<Employee | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuEmployeeId, setMenuEmployeeId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await employeeService.getAllEmployees();
        setEmployees(data);
      } catch (error) {
        setSnackbar({ open: true, message: 'Erro ao carregar funcionários.' });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (isMobile) setViewMode('card');
    else setViewMode('table');
  }, [isMobile]);

  const handleRoleFilterChange = (event: SelectChangeEvent<string>) => {
    setRoleFilter(event.target.value as EmployeeRole | 'all');
  };

  const handleOpenNew = () => {
    setEmployeeToEdit(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (emp: Employee) => {
    setEmployeeToEdit(emp);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEmployeeToEdit(null);
  };

  const handleSubmitEmployee = async (employee: any) => {
    try {
      if (employee.id) {
        const updated = await employeeService.updateEmployee(employee.id, employee);
        setEmployees((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
        setSnackbar({ open: true, message: 'Funcionário atualizado com sucesso.' });
      } else {
        const created = await employeeService.createEmployee(employee);
        setEmployees((prev) => [...prev, created]);
        setSnackbar({ open: true, message: 'Funcionário cadastrado com sucesso.' });
      }

      handleCloseModal();
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao salvar funcionário.' });
    }
  };
  const handleDelete = async (id: number) => {
    setMenuAnchor(null);
    setMenuEmployeeId(null);

    if (!window.confirm('Tem certeza que deseja excluir este funcionário?')) return;

    try {
      await employeeService.deleteEmployee(id);
      setEmployees((prev) => prev.filter((e) => e.id !== id));
      setSnackbar({ open: true, message: 'Funcionário excluído.' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao excluir funcionário.' });
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuEmployeeId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuEmployeeId(null);
  };

  const filtered = employees.filter((emp) => {
    const matchSearch =
      !searchTerm ||
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.email && emp.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchRole = roleFilter === 'all' || emp.role === roleFilter;
    return matchSearch && matchRole;
  });

  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(filtered.length / rowsPerPage) || 1;

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const totalPayroll = filtered.reduce(
    (s, e) => s + parseFloat(e.base_salary ?? 0),
    0
  );
  const sellersCount = filtered.filter((e) => e.role === 'vendedor').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Fade in timeout={600}>
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Funcionários
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Salário fixo e bônus por metas
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenNew}
            sx={{
              py: 1.2,
              px: 3,
              borderRadius: '8px',
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
              '&:hover': { boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)' },
            }}
          >
            Novo funcionário
          </Button>
        </Box>

        <EmployeeFormModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmitEmployee}
          employee={employeeToEdit}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ mt: 6 }}
        >
          <Alert severity="success" onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{ sx: { borderRadius: '8px', minWidth: 160 } }}
        >
          <MenuItem
            onClick={() => {
              const emp = employees.find((e) => e.id === menuEmployeeId);
              if (emp) handleOpenEdit(emp);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Editar</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => menuEmployeeId != null && handleDelete(menuEmployeeId)}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Excluir</ListItemText>
          </MenuItem>
        </Menu>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: '12px',
                backgroundImage: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                  <PeopleIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Total de funcionários
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {filtered.length}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {filtered.length === 1 ? '1 funcionário' : `${filtered.length} funcionários`}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: '12px',
                backgroundImage: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                  <PeopleIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Vendedores
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {sellersCount}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {sellersCount === 1 ? '1 vendedor' : `${sellersCount} vendedores`}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderRadius: '12px',
                backgroundImage: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                  <MoneyIcon />
                </Avatar>
                <Typography variant="h6" color="text.secondary">
                  Folha (salários base)
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color={theme.palette.mode === 'dark' ? 'white' : 'text.primary'}>
                {formatCurrency(totalPayroll)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                total mensal
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: '12px',
            backdropFilter: 'blur(4px)',
            background: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Buscar por nome ou e-mail..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <InputLabel>Cargo</InputLabel>
                <Select
                  label="Cargo"
                  value={roleFilter}
                  onChange={handleRoleFilterChange}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  {(Object.entries(EMPLOYEE_ROLES) as [EmployeeRole, string][]).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {filtered.length === 0 ? (
          <Paper
            sx={{
              p: 5,
              textAlign: 'center',
              borderRadius: '12px',
              backgroundImage: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(80, 80, 80, 0.1) 0%, rgba(0, 0, 0, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(240, 240, 240, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            }}
          >
            <PeopleIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
            <Typography variant="h6">Nenhum funcionário encontrado</Typography>
            <Typography variant="body2" color="text.secondary">
              Ajuste os filtros ou cadastre um novo funcionário.
            </Typography>
          </Paper>
        ) : viewMode === 'table' ? (
          <>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                mb: 3,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
                    }}
                  >
                    <TableCell>Nome</TableCell>
                    <TableCell>Cargo</TableCell>
                    <TableCell align="right">Salário base</TableCell>
                    <TableCell align="center">Meta</TableCell>
                    <TableCell align="right">Bônus meta</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((emp) => (
                    <TableRow
                      key={emp.id}
                      hover
                      sx={{
                        '&:hover': { bgcolor: 'rgba(63, 136, 197, 0.04)' },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: theme.palette.primary.main }}>
                            {emp.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography fontWeight="medium">{emp.name}</Typography>
                            {emp.email && (
                              <Typography variant="body2" color="text.secondary">
                                {emp.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={<RoleIcon sx={{ fontSize: 18 }} />}
                          label={EMPLOYEE_ROLES[emp.role]}
                          size="small"
                          sx={{ borderRadius: '8px' }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                        {formatCurrency(emp.base_salary)}
                      </TableCell>
                      <TableCell align="center">
                        {emp.monthly_sales_target != null ? (
                          emp.target_in_units
                            ? `${emp.monthly_sales_target} un.`
                            : formatCurrency(emp.monthly_sales_target)
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {emp.target_bonus != null ? formatCurrency(emp.target_bonus) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ações">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, emp.id)}
                            sx={{ '&:hover': { bgcolor: 'rgba(63, 136, 197, 0.08)' } }}
                          >
                            <MoreIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        ) : (
          <Grid container spacing={3}>
            {paginated.map((emp, index) => (
              <Grid item xs={12} sm={6} md={4} key={emp.id}>
                <Zoom in style={{ transitionDelay: `${index * 80}ms` }}>
                  <Card
                    sx={{
                      borderRadius: '16px',
                      overflow: 'hidden',
                      minHeight: 280,
                      transition: 'all 0.3s',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, emp.id)}
                        sx={{
                          bgcolor: 'background.paper',
                          boxShadow: 1,
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <MoreIcon />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        height: 90,
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(63, 136, 197, 0.4) 0%, rgba(0,0,0,0.3) 100%)'
                          : 'linear-gradient(135deg, rgba(63, 136, 197, 0.15) 0%, rgba(240,240,240,0.9) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: theme.palette.primary.main,
                          fontSize: '1.25rem',
                        }}
                      >
                        {emp.name.charAt(0)}
                      </Avatar>
                    </Box>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {emp.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={EMPLOYEE_ROLES[emp.role]}
                        sx={{ mb: 1.5, borderRadius: '8px' }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Salário: <strong>{formatCurrency(emp.base_salary)}</strong>
                        </Typography>
                        {emp.monthly_sales_target != null && (
                          <Typography variant="body2" color="text.secondary">
                            Meta:{' '}
                            <strong>
                              {emp.target_in_units
                                ? `${emp.monthly_sales_target} un.`
                                : formatCurrency(emp.monthly_sales_target)}
                            </strong>
                          </Typography>
                        )}
                        {emp.target_bonus != null && (
                          <Typography variant="body2" color="text.secondary">
                            Bônus meta: <strong>{formatCurrency(emp.target_bonus)}</strong>
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
            {totalPages > 1 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </Fade>
  );
};

export default EmployeeList;
